<script lang="ts">
  import type { ActiveCampaign } from '@agapi/mlc-core';
  import { css } from '@agapi/ui/panda/css';
  import { hstack, stack } from '@agapi/ui/panda/patterns';
  import { ArrowRight } from 'phosphor-svelte';
  import { Card, CardContent } from '@agapi/ui/card';
  import * as m from '$lib/parachute/messages';
  import { page } from '$app/state';
  import { getLocaleFromUrl } from '../../utils/locale.shared';

  interface Props {
    activeCampaign?: ActiveCampaign;
    isShortDated?: boolean;
    bestBeforeDate?: string;
    daysUntilBestBefore?: number;
    daysUntilCampaignEnd?: number;
    class?: string;
  }

  const { activeCampaign, isShortDated, bestBeforeDate, daysUntilBestBefore, daysUntilCampaignEnd, class: className }: Props = $props();


  // Format the best before date using user's locale
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = getLocaleFromUrl(page.url);
    return date.toLocaleDateString(locale.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate the campaign countdown display using $derived for performance
  const campaignCountdownDisplay = $derived(() => {
    if (!daysUntilCampaignEnd || daysUntilCampaignEnd < 0) return null;

    if (daysUntilCampaignEnd === 0) {
      return m.campaignExpiresToday();
    } else if (daysUntilCampaignEnd === 1) {
      return m.campaignExpiresTomorrow();
    } else {
      return m.campaignDaysLeft({ days: daysUntilCampaignEnd });
    }
  });
</script>

{#if activeCampaign}
  <a
    href={activeCampaign.url}
    class={css({
      display: 'block',
      textDecoration: 'none',
      color: 'inherit',
      _hover: {
        opacity: 0.95
      },
      cursor: 'pointer'
    })}
    aria-label={m.viewCampaignDetails()}
  >
    <Card
      colorVariant="tertiary.subdued"
      spacing="tight"
    >
      <CardContent>
        <div class={hstack({ gap: '4', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' })}>
          <div class={stack({ gap: '2', flex: 1 })}>
            <!-- Campaign title (without "Kampanj:" prefix) -->
            <h3 class={css({
              fontSize: 'lg',
              fontWeight: 'semibold',
              lineHeight: 'tight',
              margin: 0
            })}>
              {activeCampaign.title}
            </h3>

            <!-- Countdown and expiry info -->
            <div class={hstack({ gap: '2', alignItems: 'center', flexWrap: 'wrap' })}>
              {#if isShortDated && bestBeforeDate}
                <!-- For short-dated products: show best-before date -->
                <span class={css({
                  fontSize: 'sm',
                  fontWeight: 'medium',
                  opacity: 0.9
                })}>
                  {m.shortDatedCampaignText({ date: formatDate(bestBeforeDate) })}
                </span>
              {:else if campaignCountdownDisplay}
                <!-- For regular campaigns: show campaign countdown -->
                <span class={css({
                  fontSize: 'sm',
                  fontWeight: 'medium',
                  opacity: 0.8
                })}>
                  {campaignCountdownDisplay()}
                </span>
              {/if}
            </div>
          </div>

          <!-- View campaign link -->
          <div class={css({
            display: 'flex',
            alignItems: 'center',
            gap: '2',
            fontSize: 'sm',
            fontWeight: 'medium',
            textDecoration: 'underline',
            flexShrink: 0,
            opacity: 0.9
          })}>
            {m.viewCampaign()}
            <ArrowRight class={css({ width: '4', height: '4' })} />
          </div>
        </div>
      </CardContent>
    </Card>
  </a>
{/if}
